import React from 'react';
import { Link } from 'react-router-dom';

/**
 * PrivacyPolicyPage - Explains how user data is collected, used, and protected
 */
const PrivacyPolicyPage: React.FC = () => {
  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <h1 className="display-5 mb-4">Privacy Policy</h1>
          <p className="text-muted mb-4">Last updated: December 2025</p>

          <div className="card mb-4" style={{
            backgroundColor: '#F0F7F4',
            border: '1px solid #7FA99B'
          }}>
            <div className="card-body">
              <h5 className="card-title" style={{ color: '#1C2826' }}>
                <i className="fas fa-shield-alt me-2" style={{ color: '#7FA99B' }}></i>
                Your Privacy at a Glance
              </h5>
              <ul className="mb-0">
                <li><strong>Publicly visible:</strong> Your Alias Name (or &ldquo;Anonymous&rdquo; if no alias provided) and City/State</li>
                <li><strong>Never shared publicly:</strong> Your real name and email address</li>
                <li><strong>We do not sell your data</strong> to third parties</li>
              </ul>
            </div>
          </div>

          <section className="mb-5">
            <h2>1. Information We Collect</h2>
            <p>When you submit a note or create an account, we collect:</p>

            <h5>For Note Submissions</h5>
            <ul>
              <li><strong>First and Last Name</strong> - Used for identification and moderation purposes only</li>
              <li><strong>Email Address</strong> - Used for account verification and important communications</li>
              <li><strong>Alias Name (optional)</strong> - This is the name displayed publicly with your note</li>
              <li><strong>Zipcode</strong> - Used to derive city/state for geographic context; the zipcode itself is not displayed</li>
              <li><strong>Note Content</strong> - The uplifting message you choose to share</li>
            </ul>

            <h5>For Account Holders</h5>
            <ul>
              <li>Google account information (name, email, profile picture) if you sign in with Google</li>
              <li>Usage data such as notes created, donations made, and display interactions</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2>2. What Is Displayed Publicly</h2>
            <p>When your note is approved and displayed on our platform or digital displays:</p>
            <ul>
              <li><strong>Alias Name</strong> - If provided during submission</li>
              <li><strong>&ldquo;Anonymous&rdquo;</strong> - If no alias was provided (your first and last name are never shown)</li>
              <li><strong>City and State</strong> - Derived from your zipcode (e.g., "Seattle, WA")</li>
              <li><strong>Your Note Content</strong> - The message you submitted</li>
            </ul>

            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Important:</strong> Your last name, email address, and exact zipcode are <strong>never</strong> displayed publicly.
            </div>
          </section>

          <section className="mb-5">
            <h2>3. How We Use Your Information</h2>
            <ul>
              <li><strong>Note Moderation:</strong> Our team reviews all notes before public display to ensure they meet our community guidelines</li>
              <li><strong>Communication:</strong> We may contact you regarding your note submission or account</li>
              <li><strong>Analytics:</strong> We track aggregate metrics to understand how notes are impacting our community</li>
              <li><strong>Improvement:</strong> We use anonymized data to improve our platform and services</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2>4. Information We Do NOT Share</h2>
            <p>We are committed to protecting your privacy:</p>
            <ul>
              <li>We do <strong>not</strong> sell your personal information to third parties</li>
              <li>We do <strong>not</strong> share your email address with third parties for marketing</li>
              <li>We do <strong>not</strong> display your full name without your explicit consent (via alias)</li>
              <li>We do <strong>not</strong> share your exact location (zipcode)</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2>5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information:</p>
            <ul>
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Secure database storage</li>
              <li>Regular security audits</li>
              <li>Limited employee access to personal data</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update inaccurate information</li>
              <li><strong>Deletion:</strong> Request removal of your data and notes</li>
              <li><strong>Withdrawal:</strong> Remove your note from public display at any time</li>
            </ul>
            <p>To exercise these rights, please contact us at <a href="mailto:service@stampoutsuicide.org">service@stampoutsuicide.org</a>.</p>
          </section>

          <section className="mb-5">
            <h2>7. Cookies and Tracking</h2>
            <p>We use minimal cookies necessary for:</p>
            <ul>
              <li>Session management (keeping you logged in)</li>
              <li>Security (preventing unauthorized access)</li>
              <li>Basic analytics (understanding site usage)</li>
            </ul>
            <p>We do not use tracking cookies for advertising purposes.</p>
          </section>

          <section className="mb-5">
            <h2>8. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting a notice on our website.</p>
          </section>

          <section className="mb-5">
            <h2>9. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our data practices, please contact:</p>
            <p>
              <strong>StampOutSuicide.org</strong><br />
              Email: <a href="mailto:service@stampoutsuicide.org">service@stampoutsuicide.org</a>
            </p>
          </section>

          <div className="text-center mt-5">
            <Link to="/" className="btn btn-outline-primary me-2" style={{
              borderColor: '#7FA99B',
              color: '#7FA99B'
            }}>
              Return Home
            </Link>
            <Link to="/submit" className="btn btn-primary" style={{
              backgroundColor: '#E07A5F',
              borderColor: '#E07A5F'
            }}>
              Submit a Note
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
